import logger from "@/helpers/logger";

/**
 * Time window for tracking 500 errors (in minutes).
 * Errors older than this are automatically removed.
 */
export const ERROR_WINDOW_MINS = 5;

export const ERROR_WINDOW_MS = ERROR_WINDOW_MINS * 60 * 1000;

/**
 * Threshold for triggering a user warning.
 * If this many 500 errors occur within the time window, show a warning.
 */
const ERROR_THRESHOLD = 3;

/**
 * Minimum time between warning notifications (in milliseconds).
 * Prevents warning spam if errors continue after threshold is exceeded.
 */
const WARNING_DEBOUNCE_MS = ERROR_WINDOW_MS;

type ErrorRecord = {
  timestamp: number;
};

/**
 * In-memory store for tracking Cube API 500 errors.
 * Maintains a time-windowed list of error timestamps.
 */
const errorRecords: ErrorRecord[] = [];
let lastWarningTimestamp = 0;

/**
 * Remove error records that are outside the time window.
 */
const cleanExpiredErrors = (): void => {
  const cutoff = Date.now() - ERROR_WINDOW_MS;
  let removed = 0;

  while (errorRecords.length > 0 && errorRecords[0].timestamp < cutoff) {
    errorRecords.shift();
    removed++;
  }

  if (removed > 0) {
    logger.log(`[Cube500Tracker] Cleaned ${removed} expired error(s)`, {
      remaining: errorRecords.length,
    });
  }
};

/**
 * Record a 500 error occurrence and check if warning threshold is exceeded.
 *
 * @returns `true` if the warning threshold has been exceeded and a warning should be shown
 */
export const track500Error = (): boolean => {
  const now = Date.now();

  // Add the new error
  errorRecords.push({ timestamp: now });

  // Clean up expired errors
  cleanExpiredErrors();

  const errorCount = errorRecords.length;

  logger.warn(`[Cube500Tracker] 500 error detected`, {
    count: errorCount,
    threshold: ERROR_THRESHOLD,
    windowMinutes: ERROR_WINDOW_MS / 60000,
  });

  // Check if threshold exceeded and enough time has passed since last warning
  if (errorCount >= ERROR_THRESHOLD) {
    const timeSinceLastWarning = now - lastWarningTimestamp;

    if (timeSinceLastWarning >= WARNING_DEBOUNCE_MS) {
      logger.debug(`[Cube500Tracker] Threshold exceeded, triggering warning`, {
        errorCount,
        threshold: ERROR_THRESHOLD,
        timeSinceLastWarning: Math.round(timeSinceLastWarning / 1000),
      });
      lastWarningTimestamp = now;
      return true;
    } else {
      logger.debug(
        `[Cube500Tracker] Threshold exceeded but warning debounced`,
        {
          timeSinceLastWarning: Math.round(timeSinceLastWarning / 1000),
          debounceSeconds: WARNING_DEBOUNCE_MS / 1000,
        }
      );
    }
  }

  return false;
};

/**
 * Reset the error tracker.
 * Called when the user dismisses the warning to allow re-notification if issues persist.
 */
export const resetTracker = (): void => {
  const previousCount = errorRecords.length;
  errorRecords.length = 0;
  lastWarningTimestamp = 0;

  logger.log(`[Cube500Tracker] Tracker reset`, {
    clearedErrors: previousCount,
  });
};

/**
 * Get current error count (for testing/debugging).
 */
export const getErrorCount = (): number => {
  cleanExpiredErrors();
  return errorRecords.length;
};
